import React, { useState, useEffect, useMemo } from 'react'
import { 
  BarChart3, 
  Tv, 
  Clock, 
  Monitor, 
  Users, 
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  Calendar,
  Filter,
  Download,
  Eye,
  Play,
  Pause,
  AlertTriangle
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import channelStatisticsService from '@/utils/services/ChannelStatisticsService'
import { hasAdminPrivileges } from '@/utils/adminUtils'

const ChannelStatistics = () => {
  const { currentUserData } = useAuth()
  const [channels, setChannels] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedPeriod, setSelectedPeriod] = useState('30d')
  const [sortBy, setSortBy] = useState('totalWatchTime')
  const [sortOrder, setSortOrder] = useState('desc')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedChannel, setSelectedChannel] = useState(null)
  const [showDeviceDetails, setShowDeviceDetails] = useState(false)

  // Check if user has admin privileges
  useEffect(() => {
    if (!hasAdminPrivileges(currentUserData)) {
      // Redirect or show access denied
      return
    }
  }, [currentUserData])

  // Load channel statistics
  useEffect(() => {
    const loadChannelStatistics = async () => {
      try {
        setLoading(true)
        const data = await channelStatisticsService.getChannelStatistics(null, selectedPeriod)
        setChannels(data)
      } catch (error) {
        console.error('Error loading channel statistics:', error)
      } finally {
        setLoading(false)
      }
    }

    if (hasAdminPrivileges(currentUserData)) {
      loadChannelStatistics()
    }
  }, [selectedPeriod, currentUserData])

  // Sort and filter channels
  const processedChannels = useMemo(() => {
    let filtered = channels.filter(channel => 
      channel.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      channel.description?.toLowerCase().includes(searchTerm.toLowerCase())
    )

    // Sort channels
    filtered.sort((a, b) => {
      const aValue = a.statistics?.[sortBy] || 0
      const bValue = b.statistics?.[sortBy] || 0
      
      if (sortOrder === 'asc') {
        return aValue - bValue
      } else {
        return bValue - aValue
      }
    })

    return filtered
  }, [channels, searchTerm, sortBy, sortOrder])

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!channels.length) return null

    const totalChannels = channels.length
    const activeChannels = channels.filter(ch => ch.isActive).length
    const totalWatchTime = channels.reduce((sum, ch) => sum + (ch.statistics?.totalWatchTime || 0), 0)
    const totalSessions = channels.reduce((sum, ch) => sum + (ch.statistics?.totalSessions || 0), 0)
    const totalDevices = channels.reduce((sum, ch) => sum + (ch.statistics?.uniqueDevices || 0), 0)
    const activeSessions = channels.reduce((sum, ch) => sum + (ch.statistics?.activeSessions || 0), 0)
    const totalErrors = channels.reduce((sum, ch) => sum + (ch.statistics?.totalErrors || 0), 0)
    const averagePingCount = channels.reduce((sum, ch) => sum + (ch.statistics?.averagePingCount || 0), 0) / Math.max(channels.length, 1)

    return {
      totalChannels,
      activeChannels,
      totalWatchTime,
      totalSessions,
      totalDevices,
      activeSessions,
      totalErrors,
      averagePingCount: Math.round(averagePingCount)
    }
  }, [channels])

  // Handle sort change
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
  }

  // Handle channel selection for detailed view
  const handleChannelSelect = async (channel) => {
    setSelectedChannel(channel)
    setShowDeviceDetails(true)
  }

  // Refresh data
  const handleRefresh = async () => {
    try {
      setLoading(true)
      const data = await channelStatisticsService.getChannelStatistics(null, selectedPeriod)
      setChannels(data)
    } catch (error) {
      console.error('Error refreshing data:', error)
    } finally {
      setLoading(false)
    }
  }

  // Export data
  const handleExport = () => {
    const csvData = processedChannels.map(channel => ({
      Name: channel.name,
      Description: channel.description,
      Status: channel.isActive ? 'Active' : 'Inactive',
      'Total Sessions': channel.statistics?.totalSessions || 0,
      'Total Watch Time (ms)': channel.statistics?.totalWatchTime || 0,
      'Average Watch Time (ms)': channel.statistics?.averageWatchTime || 0,
      'Unique Devices': channel.statistics?.uniqueDevices || 0,
      'Active Sessions': channel.statistics?.activeSessions || 0,
      'Total Errors': channel.statistics?.totalErrors || 0,
      'Average Ping Count': channel.statistics?.averagePingCount || 0,
      'Last Activity': channel.statistics?.lastActivity ? new Date(channel.statistics.lastActivity).toISOString() : 'Never'
    }))

    const csv = convertToCSV(csvData)
    downloadCSV(csv, `channel-statistics-${selectedPeriod}-${new Date().toISOString().split('T')[0]}.csv`)
  }



  // Convert data to CSV
  const convertToCSV = (data) => {
    const headers = Object.keys(data[0])
    const csvRows = [headers.join(',')]
    
    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header]
        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value
      })
      csvRows.push(values.join(','))
    }
    
    return csvRows.join('\n')
  }

  // Download CSV file
  const downloadCSV = (csv, filename) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // Show loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Channel Statistics</h1>
            <p className="text-gray-600">Loading channel performance data...</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="text-center py-16">
            <RefreshCw className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-spin" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Statistics</h3>
            <p className="text-gray-600">Please wait while we gather channel performance data...</p>
          </div>
        </div>
      </div>
    )
  }

  // Show access denied
  if (!hasAdminPrivileges(currentUserData)) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Channel Statistics</h1>
            <p className="text-gray-600">Access denied</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="text-center py-12">
            <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600">You don't have permission to view channel statistics.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Channel Statistics</h1>
          <p className="text-gray-600">Monitor channel performance and device usage</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>

        </div>
      </div>

      {/* Filters and Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="all">All time</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search channels..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
              />
            </div>
          </div>
          <div className="text-sm text-gray-500">
            {processedChannels.length} of {channels.length} channels
          </div>
        </div>
      </div>

      {/* Summary Statistics */}
      {summaryStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Total Channels"
            value={summaryStats.totalChannels}
            subtitle={`${summaryStats.activeChannels} active`}
            icon={Tv}
            color="blue"
          />
          <MetricCard
            title="Total Watch Time"
            value={channelStatisticsService.formatWatchTime(summaryStats.totalWatchTime)}
            subtitle={`${summaryStats.totalSessions} sessions`}
            icon={Clock}
            color="green"
          />
          <MetricCard
            title="Unique Devices"
            value={summaryStats.totalDevices}
            subtitle={`${summaryStats.activeSessions} active sessions`}
            icon={Monitor}
            color="purple"
          />
                     <MetricCard
             title="Active Sessions"
             value={summaryStats.activeSessions}
             subtitle="Currently streaming"
             icon={Activity}
             color="orange"
           />
           <MetricCard
             title="Total Errors"
             value={summaryStats.totalErrors}
             subtitle={`${summaryStats.averagePingCount} avg pings`}
             icon={AlertTriangle}
             color="red"
           />
        </div>
      )}

      {/* Channel Statistics Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Channel Performance</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Channel
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('totalSessions')}
                >
                  <div className="flex items-center gap-2">
                    Sessions
                    {sortBy === 'totalSessions' && (
                      sortOrder === 'asc' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('totalWatchTime')}
                >
                  <div className="flex items-center gap-2">
                    Watch Time
                    {sortBy === 'totalWatchTime' && (
                      sortOrder === 'asc' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />
                    )}
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('uniqueDevices')}
                >
                  <div className="flex items-center gap-2">
                    Devices
                    {sortBy === 'uniqueDevices' && (
                      sortOrder === 'asc' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />
                    )}
                  </div>
                </th>
                                 <th 
                   className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                   onClick={() => handleSort('activeSessions')}
                 >
                   <div className="flex items-center gap-2">
                     Active
                     {sortBy === 'activeSessions' && (
                       sortOrder === 'asc' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />
                     )}
                   </div>
                 </th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Errors
                 </th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Avg Pings
                 </th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Status
                 </th>
                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   Actions
                 </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {processedChannels.map((channel) => (
                <tr key={channel.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {channel.thumbnail ? (
                          <img 
                            className="h-10 w-10 rounded-lg object-cover" 
                            src={channel.thumbnail} 
                            alt={channel.name}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-gray-200 flex items-center justify-center">
                            <Tv className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{channel.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {channel.statistics?.totalSessions || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {channelStatisticsService.formatWatchTime(channel.statistics?.totalWatchTime || 0)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {channel.statistics?.uniqueDevices || 0}
                  </td>
                                     <td className="px-6 py-4 whitespace-nowrap">
                     <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                       (channel.statistics?.activeSessions || 0) > 0 
                         ? 'bg-green-100 text-green-800' 
                         : 'bg-gray-100 text-gray-800'
                     }`}>
                       {channel.statistics?.activeSessions || 0}
                     </span>
                   </td>
                   <td className="px-6 py-4 whitespace-nowrap">
                     <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                       (channel.statistics?.totalErrors || 0) > 0 
                         ? 'bg-red-100 text-red-800' 
                         : 'bg-green-100 text-green-800'
                     }`}>
                       {channel.statistics?.totalErrors || 0}
                     </span>
                   </td>
                   <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                     {channel.statistics?.averagePingCount || 0}
                   </td>
                   <td className="px-6 py-4 whitespace-nowrap">
                     <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                       channel.isActive 
                         ? 'bg-green-100 text-green-800' 
                         : 'bg-red-100 text-red-800'
                     }`}>
                       {channel.isActive ? 'Active' : 'Inactive'}
                     </span>
                   </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleChannelSelect(channel)}
                      className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                    >
                      <Eye className="w-4 h-4" />
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {processedChannels.length === 0 && (
          <div className="text-center py-12">
            <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No channels found</h3>
            <p className="text-gray-600">
              {searchTerm ? 'Try adjusting your search terms.' : 'No channels have been created yet.'}
            </p>
          </div>
        )}
      </div>

      {/* Device Details Modal */}
      {showDeviceDetails && selectedChannel && (
        <DeviceDetailsModal
          channel={selectedChannel}
          onClose={() => {
            setShowDeviceDetails(false)
            setSelectedChannel(null)
          }}
          period={selectedPeriod}
        />
      )}
    </div>
  )
}

// Metric Card Component
const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => {
  const colorClasses = {
    blue: 'text-blue-600 bg-blue-100',
    green: 'text-green-600 bg-green-100',
    purple: 'text-purple-600 bg-purple-100',
    orange: 'text-orange-600 bg-orange-100'
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
    </div>
  )
}

// Device Details Modal Component
const DeviceDetailsModal = ({ channel, onClose, period }) => {
  const [deviceStats, setDeviceStats] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadDeviceStats = async () => {
      try {
        setLoading(true)
        const stats = await channelStatisticsService.getDeviceUsageStats(channel.id, period)
        setDeviceStats(stats)
      } catch (error) {
        console.error('Error loading device stats:', error)
      } finally {
        setLoading(false)
      }
    }

    loadDeviceStats()
  }, [channel.id, period])

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Device Details - {channel.name}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-4 animate-spin" />
                <p className="text-gray-600">Loading device details...</p>
              </div>
            ) : deviceStats ? (
              <div className="space-y-6">
                                 {/* Summary Stats */}
                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                   <div className="text-center p-4 bg-gray-50 rounded-lg">
                     <div className="text-2xl font-bold text-gray-900">{deviceStats.totalSessions}</div>
                     <div className="text-sm text-gray-600">Total Sessions</div>
                   </div>
                   <div className="text-center p-4 bg-gray-50 rounded-lg">
                     <div className="text-2xl font-bold text-gray-900">
                       {channelStatisticsService.formatWatchTime(deviceStats.totalWatchTime)}
                     </div>
                     <div className="text-sm text-gray-600">Total Watch Time</div>
                   </div>
                   <div className="text-center p-4 bg-gray-50 rounded-lg">
                     <div className="text-2xl font-bold text-gray-900">{deviceStats.uniqueDevices}</div>
                     <div className="text-sm text-gray-600">Unique Devices</div>
                   </div>
                   <div className="text-center p-4 bg-gray-50 rounded-lg">
                     <div className="text-2xl font-bold text-gray-900">{deviceStats.activeSessions}</div>
                     <div className="text-sm text-gray-600">Active Sessions</div>
                   </div>
                 </div>
                 
                 {/* Additional Stats */}
                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                   <div className="text-center p-4 bg-red-50 rounded-lg">
                     <div className="text-2xl font-bold text-red-900">{deviceStats.totalErrors}</div>
                     <div className="text-sm text-red-600">Total Errors</div>
                   </div>
                   <div className="text-center p-4 bg-blue-50 rounded-lg">
                     <div className="text-2xl font-bold text-blue-900">{deviceStats.averagePingCount}</div>
                     <div className="text-sm text-blue-600">Avg Ping Count</div>
                   </div>
                 </div>

                {/* Device List */}
                {deviceStats.deviceDetails && deviceStats.deviceDetails.length > 0 ? (
                  <div>
                    <h4 className="text-md font-medium text-gray-900 mb-3">Device Usage</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Device ID</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sessions</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Watch Time</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Seen</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {deviceStats.deviceDetails.map((device, index) => (
                            <tr key={index}>
                              <td className="px-4 py-2 text-sm text-gray-900 font-mono">{device.deviceId}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">{device.totalSessions}</td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {channelStatisticsService.formatWatchTime(device.totalWatchTime)}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {channelStatisticsService.formatDate(device.lastSeen)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Monitor className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No device data available for this period</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>Failed to load device details</p>
              </div>
            )}
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChannelStatistics
